import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { api } from '../api';
import { useAuth } from '../contexts/AuthContext';

const VerifyEmail = () => {
  const history = useHistory();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, error, expired
  const [message, setMessage] = useState('Verifying your email...');

  useEffect(() => {
    const verifyEmail = async () => {
      // Get token from URL query params
      const params = new URLSearchParams(location.search);
      const token = params.get('token');

      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. No token provided.');
        return;
      }

      try {
        const response = await api.post('/auth/verify-email', { token });
        if (response.data.verified) {
          setStatus('success');
          setMessage('Your email has been verified successfully!');
          
          // Refresh user data if logged in
          if (currentUser) {
            try {
              const token = localStorage.getItem('token');
              const userResponse = await api.get('/auth/me', {
                headers: { Authorization: token }
              });
              // Update user in context - we'll need to trigger a refresh
              window.location.reload(); // Simple way to refresh user state
            } catch (err) {
              console.error('Failed to refresh user data:', err);
            }
          }
          
          // Redirect to dashboard if logged in, otherwise to login
          setTimeout(() => {
            if (currentUser) {
              history.push('/dashboard');
            } else {
              history.push('/login');
            }
          }, 2000);
        }
      } catch (error) {
        const errorMessage = error.response?.data?.detail || 'Verification failed';
        setStatus('error');
        
        if (errorMessage.includes('expired')) {
          setMessage('This verification link has expired. Please request a new one.');
          setStatus('expired');
        } else if (errorMessage.includes('already verified')) {
          setStatus('success');
          setMessage('Your email is already verified!');
          setTimeout(() => {
            history.push('/login');
          }, 3000);
        } else {
          setMessage(errorMessage);
        }
      }
    };

    verifyEmail();
  }, [location.search, history, currentUser]);

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {status === 'verifying' && (
            <>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="h5" component="h1" gutterBottom>
                Verifying Your Email
              </Typography>
              <Typography variant="body1" color="text.secondary" align="center">
                {message}
              </Typography>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" component="h1" gutterBottom>
                Email Verified!
              </Typography>
              <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
                {message}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                Redirecting to login...
              </Typography>
              <Button
                variant="contained"
                sx={{ mt: 2 }}
                onClick={() => history.push('/login')}
              >
                Go to Login
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" component="h1" gutterBottom>
                Verification Failed
              </Typography>
              <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
                {message}
              </Alert>
              <Button
                variant="contained"
                onClick={() => history.push('/login')}
              >
                Go to Login
              </Button>
            </>
          )}

          {status === 'expired' && (
            <>
              <ErrorIcon sx={{ fontSize: 60, color: 'warning.main', mb: 2 }} />
              <Typography variant="h5" component="h1" gutterBottom>
                Link Expired
              </Typography>
              <Alert severity="warning" sx={{ mb: 2, width: '100%' }}>
                {message}
              </Alert>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                You can request a new verification email after logging in.
              </Typography>
              <Button
                variant="contained"
                onClick={() => history.push('/login')}
              >
                Go to Login
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default VerifyEmail;

